/**
 * Service which update values for footer total price.
 */
import { B_FLOW_ID, C_FLOW_ID, PL_FLOW_ID } from '../boilerplate/item-ids';

const itemA = C_FLOW_ID; // Contents product
const itemB = PL_FLOW_ID; // Private Liabilities product
const itemC = B_FLOW_ID; // Buildings product

/**
 * @typedef {Object} Input
 * @property {boolean} item_A
 * @property {boolean} item_B
 * @property {boolean} item_C
 * @property {boolean} item_A_B
 * @property {boolean} item_A_C
 * @property {boolean} item_B_C
 * @property {boolean} item_A_B_C
 * @property {boolean?} contents_finished A
 * @property {boolean?} private_liability_finished B
 * @property {boolean?} buildings_finished C
 */
function calculatePrice(kasko, quote, path = '') {
  if (!quote || !quote.data || !quote.data.extraData) {
    return;
  }

  /** @type {Input} */
  const inputState = kasko.getState().input;

  /** @type {string} */
  const itemId = kasko.getState().item?.data?.id;

  /** @type {string} */
  const currentPath = path.replace('/', '');

  if (!itemId) {
    return;
  }

  let price = 0;

  /**
   * Case to handle edge case in static flow when you start with duo/triple flow from `initial`
   * screen which contains A (Contents) product. User opens A product `summary` screen and with this
   * flag, we dont sum yet A + next product together while `inputState.contents_finished` says its
   * already finished and should be calculating also next product's price. There we have three
   * possible cases, 2 (AB and AC) for duo items, 1 for triple (ABC).
   */
  let isStaticAFlowEdgeCase = false;
  if (
    (inputState.item_A_B || inputState.item_A_C || inputState.item_A_B_C) &&
    !!inputState.contents_finished &&
    currentPath === 'summary'
  ) {
    isStaticAFlowEdgeCase = inputState.item_A_B && !inputState.private_liability_finished;
    isStaticAFlowEdgeCase =
      isStaticAFlowEdgeCase || (inputState.item_A_C && !inputState.buildings_finished);
    isStaticAFlowEdgeCase =
      isStaticAFlowEdgeCase ||
      (inputState.item_A_B_C &&
        !inputState.private_liability_finished &&
        !inputState.buildings_finished);
  }

  /**
   * Case to handle edge case in static flow when you start with duo flow from `initial` screen
   * which contains B and C products. User opens C product's `buildings-summary` screen and with
   * this flag, we dont sum yet both products together while `inputState.buildings_finished` says
   * its already finished and should be calculating also next product's price.
   */
  const isStaticCFlowEdgeCase =
    inputState.item_B_C &&
    currentPath === 'buildings-summary' &&
    !!inputState.buildings_finished &&
    !inputState.private_liability_finished;

  /**
   * Case to handle edge case in static flow when you start with triple flow from `initial` screen
   * which contains all products (ABC). User goes in flow, enters first part for A product and then
   * continue with B product flow part, opens its `private-liability-summary` and with this flag,
   * we dont sum yet all three products together while `inputState.private_liability_finished` and
   * `inputState.contents_finished` says that first two parts are already finished and we should
   * calculate all three prices together.
   */
  const isStaticBFlowEdgeCase =
    inputState.item_A_B_C &&
    currentPath === 'private-liability-summary' &&
    !!inputState.contents_finished &&
    !!inputState.private_liability_finished &&
    !inputState.buildings_finished;

  /**
   * Finished flows.
   */
  const isFlowFinished = {
    A: !!inputState.contents_finished && !isStaticAFlowEdgeCase,
    B: !!inputState.private_liability_finished && !isStaticBFlowEdgeCase,
    C: !!inputState.buildings_finished && !isStaticCFlowEdgeCase,
  };

  /**
   * Show price whenever the flow has been finished OR whenever it's a solo item.
   */
  const shouldShowPrice = {
    A: isFlowFinished.A || itemId === itemA,
    B: isFlowFinished.B || itemId === itemB,
    C: isFlowFinished.C || itemId === itemC,
  };

  /**
   * Show prices based on flow.
   * User should see price from finished + current flow.
   */

  // AB (Contents + PL)
  // default static flow, starts with A (Contents) part
  if (inputState.item_A_B && !isFlowFinished.A && !isFlowFinished.B) {
    shouldShowPrice.A = true;
    shouldShowPrice.B = false;
  }

  if (inputState.item_A_B && (isFlowFinished.A || isFlowFinished.B)) {
    shouldShowPrice.A = true;
    shouldShowPrice.B = true;
  }

  // AC (Contents + Buildings)
  // default static flow, starts with A (Contents) part
  if (inputState.item_A_C && !isFlowFinished.A && !isFlowFinished.C) {
    shouldShowPrice.A = true;
    shouldShowPrice.C = false;
  }

  if (inputState.item_A_C && (isFlowFinished.A || isFlowFinished.C)) {
    shouldShowPrice.A = true;
    shouldShowPrice.C = true;
  }

  // BC (PL + Buildings)
  // default static flow, starts with C (Buildings) part
  if (inputState.item_B_C && !isFlowFinished.B && !isFlowFinished.C) {
    shouldShowPrice.B = false;
    shouldShowPrice.C = true;
  }

  if (inputState.item_B_C && (isFlowFinished.B || isFlowFinished.C)) {
    shouldShowPrice.B = true;
    shouldShowPrice.C = true;
  }

  // ABC (Contents + PL + Buildings)
  // default static flow, starts with A (Contents) part
  if (inputState.item_A_B_C && !isFlowFinished.A && !isFlowFinished.B && !isFlowFinished.C) {
    shouldShowPrice.A = true;
    shouldShowPrice.B = false;
    shouldShowPrice.C = false;
  }

  // default static flow, starts with A (Contents) part and continue with B (PL) part
  if (inputState.item_A_B_C && isFlowFinished.A && !isFlowFinished.B && !isFlowFinished.C) {
    shouldShowPrice.A = true;
    shouldShowPrice.B = true;
    shouldShowPrice.C = false;
  }

  const finishedTwoParts =
    (isFlowFinished.A && isFlowFinished.B) ||
    (isFlowFinished.A && isFlowFinished.C) ||
    (isFlowFinished.B && isFlowFinished.C);
  if (inputState.item_A_B_C && finishedTwoParts) {
    shouldShowPrice.A = true;
    shouldShowPrice.B = true;
    shouldShowPrice.C = true;
  }

  /**
   * Finally!
   *
   * Add up item price that should be shown.
   */
  if (shouldShowPrice.A) {
    price += quote.data.extraData.yearly_content_insurance_gross_premium;
  }

  if (shouldShowPrice.B) {
    price += quote.data.extraData.yearly_private_liability_gross_premium;
  }

  if (shouldShowPrice.C) {
    price += quote.data.extraData.yearly_building_gross_premium;
  }

  kasko.dispatchEvent('write-input', {
    value: price,
    path: 'items_total_cost',
    overwrite: true,
  });
}

export default (kasko) => {
  kasko.addEventListener(
    'state-changed',
    (quote) => {
      const currentPath = kasko.getState('route').state.url;
      calculatePrice(kasko, quote, currentPath);
    },
    { slice: 'quote' },
  );

  kasko.addEventListener('route-changed', ({ current }) => {
    const { quote } = kasko.getState();
    calculatePrice(kasko, quote, current?.path);
  });
};
